/**
 * System.Ledger.types
 *
 * The double-entry journal of the reconciliation ledger and the wire contract of its callables,
 * `ledgerPostTransactions`, `ledgerListTransactions`, `ledgerAccrualBackfill`, the read views
 * `ledgerListClaimGroups`, `ledgerListClaims`, `ledgerListPayments` and `ledgerListAccountHistory`, and the
 * matching callables `ledgerProposeAllocations`, `ledgerGetProposal`, `ledgerListProposals`,
 * `ledgerEditProposal`, `ledgerCommitProposal` and `ledgerRejectProposal`, and the aging and forecast reads
 * `ledgerListAging` and `ledgerGetCashFlowForecast`, the exceptions read `ledgerListExceptions`, the definition save `ledgerSaveDefinition`, and the
 * statement-file import `ledgerProposeStatementImport` (never writes) and `ledgerConfirmStatementImport`
 * (posts the file's receipt `file:{fileHash}`, or `file:{fileHash}:import:{n}` once every earlier import of the file is
 * reversed), and the period close callables `ledgerClosePeriod`, `ledgerReopenPeriod`,
 * `ledgerListPeriods` and the scheduled `ledgerScheduledPeriodClose`, in `functions/main/src/ledger/`. A claim's terms,
 * `expectedAt` and exception flags are resolved once at accrual and stored on the claim, never re-read.
 * A proposal is review state in `ledger.proposals`, outside the journal, and changes no balance; only its
 * commit posts, as `alloc:{proposalId}:{claimId}` and `adj:{proposalId}:{targetId}`. The journal
 * lives in Postgres (`ledger.transactions`, `ledger.postings`, `ledger.accounts`), never in Firestore.
 * A receipt's remittance lines are evidence for matching and never a leg; their sum need not equal the receipt.
 * A caller sends one `LedgerTransactionInput` per kind and never raw postings: the legs come from the
 * fixed per-kind table in `ledgerTransactionPostings`. Amounts are integer minor units that must be
 * safe integers, debit positive and credit negative, and every transaction's postings sum to zero per
 * currency. A `transactionId` is the idempotency key: a second post of the same id changes nothing.
 * A period is closed by a `periodClose` transaction with no postings, `close:{periodKey}:{closeNumber}`, and
 * reopened by its reversal, so both stay in history. A transaction whose natural moment falls in a closed period
 * posts at the start of the next open period and names the closed one in `correctsPeriod`; only
 * `ledgerPlaceTransactions` sets it, and each move is reported as a `LedgerTransactionPlacement`.
 * The scheduled `ledgerScheduledLifecycle` moves resolved closure sets to `ledger_archive` and later purges them;
 * history reads still return archived rows, marked `archived`. Legal holds (`ledgerPlaceLegalHold`,
 * `ledgerReleaseLegalHold`, `ledgerListLegalHolds`) block both steps, and `ledgerListLifecycleRuns` reads each run's counts.
 */

import type { LedgerDefinition, LedgerFiscalPeriodUnit, LedgerSourceLine, LedgerTermShape } from './System.Ledger.Definition.types';
import type { LedgerProposalProblem } from './ledgerEngine/LedgerMatching.types';

export const LEDGER_TRANSACTION_KINDS = ['accrual', 'receipt', 'allocation', 'adjustment', 'reversal', 'periodClose'] as const;
export type LedgerTransactionKind = (typeof LEDGER_TRANSACTION_KINDS)[number];

/** `posted_by` of every accrual the worker posts; the colon keeps it apart from any Firebase uid. */
export const LEDGER_ACCRUAL_POSTED_BY = 'system:ledgerAccrualWorker';

/** `posted_by` of every close the scheduled job posts. */
export const LEDGER_PERIOD_CLOSE_POSTED_BY = 'system:ledgerScheduledPeriodClose';

/** One fiscal period on the wire: its key is the local start date, and `[startAt, endAt)` are ISO instants. */
export type LedgerPeriodBounds = { periodKey: string; startAt: string; endAt: string };

export const LEDGER_ACCOUNT_ROLES = ['receivable', 'revenue', 'cash', 'unapplied', 'adjustment'] as const;
export type LedgerAccountRole = (typeof LEDGER_ACCOUNT_ROLES)[number];

export type LedgerAccountRef =
    | { role: 'receivable'; claimId: string }
    | { role: 'revenue'; claimKindId: string }
    | { role: 'cash'; channelId: string }
    | { role: 'unapplied'; paymentId: string }
    | { role: 'adjustment'; reasonId: string };

/** The bound roles and dimensions at the moment of posting; null where the source binds none. */
export type LedgerTransactionSnapshot = {
    counterpartyId: string | null;
    matchGroup: string | null;
    triggerAt: string | null;
    dimensions: Record<string, string | number | null>;
};

export const LEDGER_CLAIM_EXCEPTION_FLAGS = ['noTermsRecord', 'termsConflict', 'invalidTermsRecord'] as const;
export type LedgerClaimExceptionFlag = (typeof LEDGER_CLAIM_EXCEPTION_FLAGS)[number];

/** `none` means neither a record nor a fallback supplied a shape. */
export type LedgerClaimTermsSource =
    | { from: 'record'; entityId: string; recordId: string }
    | { from: 'fallback' }
    | { from: 'none' };

/** Stored in `ledger.claims.terms` at accrual; never re-read from the record. `triggerAt` is null when the trigger moment is unknown. */
export type LedgerClaimTerms = { shape: LedgerTermShape | null; source: LedgerClaimTermsSource; triggerAt: string | null };

/** `expectedAt` is null when there is no shape or no trigger moment; the claim then ages as unscheduled. */
export type LedgerClaimTermsResolution = {
    terms: LedgerClaimTerms;
    expectedAt: string | null;
    exceptionFlags: LedgerClaimExceptionFlag[];
};

export const LEDGER_INSTRUMENTS = ['check', 'ach', 'wire', 'creditNote'] as const;
export type LedgerInstrument = (typeof LEDGER_INSTRUMENTS)[number];

/** The payer's breakdown of one receipt; evidence for matching, never a leg. */
export type LedgerRemittanceLineInput = {
    /** Signed and never zero: a negative amount is a credit line, money the payer took back on its match group. */
    amountMinor: number;
    matchGroup: string | null;
    dimensions: Record<string, string | number | null>;
};
export type LedgerRemittanceLine = LedgerRemittanceLineInput & { lineNo: number };

type LedgerTransactionInputBase = {
    transactionId: string;
    /** An ISO instant with `Z` or an explicit offset. */
    effectiveAt: string;
    snapshot: LedgerTransactionSnapshot;
    /** The source, Meta.importRow style. */
    provenance: Record<string, unknown>;
    /** Set only by ledgerPlaceTransactions; ledgerPostTransactions refuses it from a caller. */
    correctsPeriod?: string;
};

/** One transaction as posted. A positive `amountMinor` shrinks the target's open or unapplied balance. */
export type LedgerTransactionInput = LedgerTransactionInputBase & (
    | {
        kind: 'accrual';
        ruleId: string;
        /** The terms fields are set only by the accrual worker and backfill; `ledgerPostTransactions` refuses them. */
        claim: { claimId: string; claimKindId: string; sourceLine: LedgerSourceLine; grainRef: string | null } & Partial<LedgerClaimTermsResolution>;
        amountMinor: number;
        quantity: number | null;
    }
    | ({
        kind: 'receipt';
        payment: {
            paymentId: string;
            channelId: string;
            instrument: LedgerInstrument;
            reference: string | null;
            remittanceLines: LedgerRemittanceLineInput[];
        };
        amountMinor: number;
    } & (
        /** Client-minted once per entry; the transaction id is `man:{postedBy}:{entryKey}`. */
        | { entryKey: string }
        /** Posted only by `ledgerConfirmStatementImport`; the transaction id is the file id of `fileHash` and `importNumber`. */
        | { fileHash: string; importNumber: number }
    ))
    | { kind: 'allocation'; paymentId: string; claimId: string; matchRuleId: string | null; amountMinor: number; quantity: number | null }
    | ({ kind: 'adjustment'; reasonId: string; target: { claimId: string } | { paymentId: string }; amountMinor: number } & (
        /** Client-minted once per entry; the transaction id is `man:{postedBy}:{entryKey}`. */
        | { entryKey: string }
        /** Posted by a proposal's commit; the transaction id is `adj:{proposalId}:{targetId}`. */
        | { proposalId: string }
    ))
    | { kind: 'reversal'; reversesTransactionId: string }
    /** Posts no legs; its `effectiveAt` is `period.endAt`, and the transaction id is `close:{periodKey}:{closeNumber}`. */
    | { kind: 'periodClose'; period: LedgerPeriodBounds; closeNumber: number }
);

/** The receipt of one statement file, built by `ledgerStatementReceipt`. */
export type LedgerFileReceiptInput = Extract<LedgerTransactionInput, { kind: 'receipt'; fileHash: string }>;

/** The close of one period, built by `ledgerPeriodCloseTransaction`. */
export type LedgerPeriodCloseInput = Extract<LedgerTransactionInput, { kind: 'periodClose' }>;

/** One transaction moved out of a closed period: `correctsPeriod` names the closed period its `requestedEffectiveAt` fell in. */
export type LedgerTransactionPlacement = {
    transactionId: string;
    requestedEffectiveAt: string;
    effectiveAt: string;
    correctsPeriod: string;
};

export type LedgerPostingInput = { lineNo: number; account: LedgerAccountRef; amountMinor: number; quantity: number | null };

/** One stored leg. `seq` counts the account's postings from 1; `balanceAfterMinor` is its balance right after this one. */
export type LedgerPosting = {
    lineNo: number;
    accountId: string;
    role: LedgerAccountRole;
    amountMinor: number;
    currency: string;
    quantity: number | null;
    seq: number;
    balanceAfterMinor: number;
};

export type LedgerTransaction = {
    transactionId: string;
    ledgerId: string;
    kind: LedgerTransactionKind;
    effectiveAt: string;
    postedAt: string;
    postedBy: string;
    definitionRev: number;
    ruleOrChannelId: string | null;
    reversesTransactionId: string | null;
    /** The closed period this transaction corrects, when placement moved it out of that period. */
    correctsPeriod: string | null;
    /** The `reasonId` of the transaction's `adjustment:{reasonId}` leg: set on an adjustment and its reversal, null on every other transaction. */
    reasonId: string | null;
    snapshot: LedgerTransactionSnapshot;
    provenance: Record<string, unknown>;
    postings: LedgerPosting[];
    /** True when the lifecycle job moved the transaction to `ledger_archive`. */
    archived: boolean;
};

/** A claim's open amount is the balance of its `receivable` account. */
export type LedgerClaimBalance = { claimId: string; currency: string; openMinor: number; lastSeq: number };
/** A payment's unapplied amount is minus the balance of its `unapplied` account; negative means over-allocated. */
export type LedgerPaymentBalance = { paymentId: string; currency: string; unappliedMinor: number; lastSeq: number };

/** What the scheduled check found; every array is empty on a sound ledger and capped at 100 rows. */
export type LedgerBalanceCheck = {
    /** How many ledgers hold at least one transaction. */
    ledgers: number;
    accountMismatches: {
        ledgerId: string;
        accountId: string;
        balanceMinor: number;
        postingsSumMinor: number;
        lastSeq: number;
        postingsCount: number;
    }[];
    runningBalanceBreaks: { ledgerId: string; accountId: string; seq: number; balanceAfterMinor: number; runningMinor: number }[];
    unbalancedTransactions: { ledgerId: string; transactionId: string; currency: string; netMinor: number }[];
    /** Only the non-zero nets. */
    trialBalance: { ledgerId: string; currency: string; netMinor: number }[];
};

export type LedgerPostTransactionsRequest = { ledgerId: string; transactions: LedgerTransactionInput[] };
/** `placements` lists every transaction the callable moved out of a closed period. */
export type LedgerPostTransactionsResponse = { posted: number; skipped: number; placements: LedgerTransactionPlacement[] };
/**
 * Opaque to the caller, who passes `next` back unchanged. The last Order form a backfill call examined, within the pass
 * of `depth` (0 for forms not created from another, then copies by `meta.createdFromFormId` chain length) and the
 * definition's sorted `source.formTypeIds`; `deeper` records that this pass has seen a form a later pass must process.
 */
export type LedgerAccrualBackfillCursor = { depth: number; formTypeId: string; afterFormId: string | null; deeper: boolean };
export type LedgerAccrualBackfillRequest = { ledgerId: string; cursor?: LedgerAccrualBackfillCursor | null };
/**
 * `forms` counts Order forms and `scanned` their rows; every other field counts one row–rule pair, for each rule whose
 * `source` names the row's table and form type. `copiedFromClaimed` counts the pairs of a line copied from another form
 * (`meta.createdFromFormId`, followed up the chain) whose source line with the same table and row id holds that rule's claim.
 */
export type LedgerAccrualBackfillCounts = {
    forms: number;
    scanned: number;
    posted: number;
    alreadyPosted: number;
    notClaimable: number;
    beforeStartAt: number;
    copiedFromClaimed: number;
    refused: number;
};
export type LedgerAccrualBackfillResponse = { counts: LedgerAccrualBackfillCounts; next: LedgerAccrualBackfillCursor | null };
/** `definition.rev` is the rev the client edited; on create it must still validate (≥ 1) and is stored as 1. */
export type LedgerSaveDefinitionRequest = { mode: 'create' | 'update'; definition: LedgerDefinition };
export type LedgerSaveDefinitionResponse = { rev: number; revBumped: boolean };
export type LedgerListTransactionsRequest = { ledgerId: string; limit?: number };
export type LedgerListTransactionsResponse = { transactions: LedgerTransaction[] };

export type LedgerClaimTotals = { accruedMinor: number; settledMinor: number; adjustedMinor: number; openMinor: number };
/** `'open'` admits the claims whose open amount is non-zero. */
export type LedgerClaimStatusFilter = 'open' | 'all';
/** `matchGroup` is null on the counterparty level. `claims` and the totals cover the claims the filter admits. */
export type LedgerClaimGroup = LedgerClaimTotals & { counterpartyId: string; matchGroup: string | null; claims: number; openClaims: number };
export type LedgerClaimSummary = LedgerClaimTotals & {
    claimId: string;
    claimKindId: string;
    counterpartyId: string;
    matchGroup: string;
    sourceLine: LedgerSourceLine;
    grainRef: string | null;
    accruedByTransactionId: string;
    accruedAt: string;
    reversed: boolean;
    lastSeq: number;
    /** Null, null and `[]` on a claim accrued without terms. */
    terms: LedgerClaimTerms | null;
    expectedAt: string | null;
    exceptionFlags: LedgerClaimExceptionFlag[];
};
export type LedgerPaymentTotals = { receivedMinor: number; allocatedMinor: number; adjustedMinor: number; unappliedMinor: number };
export type LedgerPaymentSummary = LedgerPaymentTotals & {
    paymentId: string;
    channelId: string;
    /** Null only on a payment received before instruments were recorded. */
    instrument: LedgerInstrument | null;
    reference: string | null;
    counterpartyId: string | null;
    receivedAt: string;
    receivedByTransactionId: string;
    reversed: boolean;
    lastSeq: number;
    remittanceLines: LedgerRemittanceLine[];
    /** The files a statement import kept, read from its receipt's provenance; null on a receipt not imported from a file. */
    statementFiles: LedgerStatementFiles | null;
};
/** A file kept under `ledgerFiles/{ledgerId}/`, named by its upload's display name. */
export type LedgerStoredFile = { fileName: string; storagePath: string };
/** `original` is null on an import posted without the original statement as received. */
export type LedgerStatementFiles = { converted: LedgerStoredFile; original: LedgerStoredFile | null };
export type LedgerPaymentsCursor = { receivedAt: string; paymentId: string };
/**
 * One posting on an account, with `reversedByTransactionId` set when a reversal of its transaction exists, `correctsPeriod` set
 * when placement moved its transaction out of that closed period, and `reasonId` set when its transaction has an
 * `adjustment:{reasonId}` leg (an adjustment or its reversal), read from that leg's account whichever account is listed.
 */
export type LedgerAccountHistoryEntry = {
    seq: number;
    transactionId: string;
    kind: LedgerTransactionKind;
    lineNo: number;
    amountMinor: number;
    quantity: number | null;
    balanceAfterMinor: number;
    effectiveAt: string;
    postedAt: string;
    postedBy: string;
    reversesTransactionId: string | null;
    reversedByTransactionId: string | null;
    correctsPeriod: string | null;
    reasonId: string | null;
    archived: boolean;
};

/** `status` defaults to `'open'`; `after` is the last group's `counterpartyId`, or its `matchGroup` when `counterpartyId` is given. */
export type LedgerListClaimGroupsRequest = {
    ledgerId: string;
    counterpartyId?: string | null;
    status?: LedgerClaimStatusFilter;
    after?: string | null;
    limit?: number;
};
export type LedgerListClaimGroupsResponse = { currency: string; groups: LedgerClaimGroup[]; next: string | null };
/** `status` defaults to `'open'`; `after` is the last `claimId`. */
export type LedgerListClaimsRequest = {
    ledgerId: string;
    counterpartyId: string;
    matchGroup: string;
    status?: LedgerClaimStatusFilter;
    after?: string | null;
    limit?: number;
};
export type LedgerListClaimsResponse = { currency: string; claims: LedgerClaimSummary[]; next: string | null };
/** `status` defaults to `'unapplied'`, which admits a non-zero unapplied amount, over-allocated included; newest received first. */
export type LedgerListPaymentsRequest = {
    ledgerId: string;
    counterpartyId?: string | null;
    status?: 'unapplied' | 'all';
    after?: LedgerPaymentsCursor | null;
    limit?: number;
};
export type LedgerListPaymentsResponse = { currency: string; payments: LedgerPaymentSummary[]; next: LedgerPaymentsCursor | null };
/** Entries in ascending `seq`; `afterSeq` and `next` are the last `seq` read. */
export type LedgerListAccountHistoryRequest = { ledgerId: string; account: LedgerAccountRef; afterSeq?: number | null; limit?: number };
export type LedgerListAccountHistoryResponse = {
    accountId: string;
    currency: string;
    balanceMinor: number;
    lastSeq: number;
    /** True when the account itself moved to `ledger_archive` with its closure set. */
    archived: boolean;
    entries: LedgerAccountHistoryEntry[];
    next: number | null;
};

/** `pastExpected` spans `[fromDays, toDays)` whole local days past `expectedAt`; `notYetDue` is day 0 or before. */
export type LedgerAgingBucket =
    | { bucket: 'unscheduled' }
    | { bucket: 'notYetDue' }
    | { bucket: 'pastExpected'; fromDays: number; toDays: number | null };
export type LedgerOpenClaimsTotal = { openMinor: number; claims: number };
/** `buckets` is aligned with the response's `buckets`. */
export type LedgerAgingCounterparty = LedgerOpenClaimsTotal & {
    counterpartyId: string;
    pastExpectedMinor: number;
    maxDaysPastExpected: number | null;
    buckets: LedgerOpenClaimsTotal[];
};
export type LedgerListAgingRequest = { ledgerId: string };
/** Open claims only; `counterparties` are ranked by `pastExpectedMinor`, then `maxDaysPastExpected`, then `counterpartyId`. */
export type LedgerListAgingResponse = {
    currency: string;
    asOf: string;
    timeZone: string;
    buckets: LedgerAgingBucket[];
    totals: LedgerOpenClaimsTotal[];
    counterparties: LedgerAgingCounterparty[];
};
export type LedgerCashFlowPeriod = LedgerOpenClaimsTotal & { index: number; startAt: string; endAt: string };
/** `periods` defaults to 6, clamped to 1..24, starting at the period holding `asOf`. */
export type LedgerGetCashFlowForecastRequest = { ledgerId: string; periods?: number };
/** Open claim amounts by the period of their `expectedAt`; `beforePeriods` is due before the first period, `later` after the last. */
export type LedgerGetCashFlowForecastResponse = {
    currency: string;
    asOf: string;
    timeZone: string;
    unit: LedgerFiscalPeriodUnit;
    unscheduled: LedgerOpenClaimsTotal;
    beforePeriods: LedgerOpenClaimsTotal;
    periods: LedgerCashFlowPeriod[];
    later: LedgerOpenClaimsTotal;
};

export const LEDGER_EXCEPTION_KINDS = ['overPaid', 'late', 'flagged', 'unappliedCash'] as const;
export type LedgerExceptionKind = (typeof LEDGER_EXCEPTION_KINDS)[number];
export type LedgerExceptionCounts = Record<LedgerExceptionKind, number>;
/** `rank` is the minor units over-paid, the days late, the absolute open minor units, or the days since received; pages order by `rank` descending, then `id`. */
export type LedgerExceptionsCursor = { rank: number; id: string };
/** `daysPastExpected` is whole local days past `expectedAt`, null when unscheduled, zero or negative when not yet due. */
export type LedgerExceptionClaim = LedgerClaimSummary & { daysPastExpected: number | null };
export type LedgerExceptionPayment = LedgerPaymentSummary & { daysSinceReceived: number };
/** `limit` defaults to 100 and is clamped to 1..1000. */
export type LedgerListExceptionsRequest = {
    ledgerId: string;
    exception: LedgerExceptionKind;
    after?: LedgerExceptionsCursor | null;
    limit?: number;
};
/** `payments` is filled only for `unappliedCash`, `claims` only for the other kinds; the other list is `[]`. */
export type LedgerListExceptionsResponse = {
    currency: string;
    asOf: string;
    timeZone: string;
    unappliedAfterDays: number;
    exception: LedgerExceptionKind;
    counts: LedgerExceptionCounts;
    claims: LedgerExceptionClaim[];
    payments: LedgerExceptionPayment[];
    next: LedgerExceptionsCursor | null;
};

export const LEDGER_PROPOSAL_STATUSES = ['proposed', 'edited', 'accepted', 'rejected'] as const;
export type LedgerProposalStatus = (typeof LEDGER_PROPOSAL_STATUSES)[number];
/** `openMinor` and `lastSeq` are the claim's balance when the line was proposed or last edited. */
export type LedgerProposalLine = {
    claimId: string;
    /**
     * Signed: a negative amount takes money back from the claim into the payment's unapplied cash (a credit line), and
     * zero posts no allocation, standing only to carry the claim's adjustment.
     */
    amountMinor: number;
    matchRuleId: string | null;
    remittanceLineNos: number[];
    score: number | null;
    openMinor: number;
    lastSeq: number;
};
/**
 * A claim the engine found but did not place; never scored in v1. `keyMatch` claims share a key with a remittance line
 * or the receipt total the payment had no unapplied cash left to fund; `amountOnly` claims match an unmatched amount.
 */
export type LedgerProposalCandidate = {
    claimId: string;
    openMinor: number;
    basis: 'keyMatch' | 'amountOnly';
    matchRuleId: string | null;
};
/**
 * At most one per target in a proposal; a positive `amountMinor` shrinks the target's open or unapplied balance, and a
 * negative one grows it, so a negative payment-target adjustment adds to what the payment can allocate.
 */
export type LedgerProposalAdjustment = { reasonId: string; target: { claimId: string } | { paymentId: string }; amountMinor: number };
/**
 * One gap of the proposal, as of its last propose or edit (`ledgerProposalDifferences`). A `shortPaid` or `overPaid`
 * gap is one key group: the claims one match rule placed under one key, with the remittance lines they cite (none for
 * a receipt total). `differenceMinor` is `paidMinor - openMinor`; a short pay is reported whether or not it is within
 * `toleranceMinor`, an over pay only within it, and the rest of an over pay shows in the `unapplied` gap.
 * `pointsTo` is `'counterparty'` on a short pay and `'internal'` on an over pay. An `unmatchedRemittanceLine` is a
 * positive line no line cites. An `unplacedCredit` is the part of a match group's credit lines (`matchGroup`, null for
 * credits with no match group) that no Return under the definition's `creditLineReasonId` and no negative payment adjustment under that
 * reason covers
 * (`ledgerUnplacedCredits`), signed negative like the credit; commit is refused (`unplacedCredit`) while one stands,
 * whatever the unapplied sum. `overAllocated` is the amount by which the allocations and payment adjustments exceed the
 * payment's unapplied cash, as when an unplaced credit leaves the positive lines wanting more than the check holds.
 */
export type LedgerMatchDifference =
    | {
        kind: 'shortPaid' | 'overPaid';
        matchRuleId: string;
        remittanceLineNos: number[];
        claimIds: string[];
        paidMinor: number;
        openMinor: number;
        differenceMinor: number;
        toleranceMinor: number;
        pointsTo: 'counterparty' | 'internal';
    }
    | { kind: 'unmatchedRemittanceLine'; remittanceLineNo: number; amountMinor: number }
    | { kind: 'unplacedCredit'; matchGroup: string | null; amountMinor: number }
    | { kind: 'unapplied'; amountMinor: number }
    | { kind: 'overAllocated'; amountMinor: number };
/** `unappliedMinor` and `paymentLastSeq` are the payment's balance when proposed or last edited. */
export type LedgerProposal = {
    proposalId: string;
    paymentId: string;
    status: LedgerProposalStatus;
    revision: number;
    scoredBy: 'rules';
    definitionRev: number;
    lines: LedgerProposalLine[];
    adjustments: LedgerProposalAdjustment[];
    candidates: LedgerProposalCandidate[];
    differences: LedgerMatchDifference[];
    unappliedMinor: number;
    paymentLastSeq: number;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
    committedAt: string | null;
    committedBy: string | null;
};
export type LedgerProposalsCursor = { createdAt: string; proposalId: string };
export type LedgerProposeAllocationsRequest = { ledgerId: string; paymentId: string };
export type LedgerProposalResponse = { currency: string; proposal: LedgerProposal };
export type LedgerGetProposalRequest = { ledgerId: string; proposalId: string };
/**
 * The most claims a payment's counterparty may hold for its proposal to be matched, re-validated or edited: propose and
 * edit refuse above it, and get answers the stored proposal with `tooManyClaims`. Commit has no cap.
 */
export const LEDGER_PROPOSAL_MAX_CLAIMS = 5000;
/** What the review names a claim by: its match group (`matchGroup`) and its claim kind. */
export type LedgerProposalClaim = Pick<LedgerClaimSummary, 'claimId' | 'claimKindId' | 'matchGroup'>;
/**
 * `problem` re-validates an open proposal against current balances; null when it fits, or when the proposal is closed.
 * `counterpartyId`, `instrument` and `reference` are the proposal's payment's, whatever the proposal's status.
 * `claims` names every claim the proposal's lines, candidates and differences cite; a claim no longer on the ledger is absent.
 */
export type LedgerGetProposalResponse = LedgerProposalResponse & {
    problem: LedgerProposalProblem | null;
    counterpartyId: string | null;
    instrument: LedgerInstrument | null;
    reference: string | null;
    claims: LedgerProposalClaim[];
    /** The open proposal's counterparty holds more than LEDGER_PROPOSAL_MAX_CLAIMS claims: answered as stored, and an edit cannot be saved. */
    tooManyClaims: boolean;
};
/** `status` defaults to `'open'` (proposed or edited); newest first. */
export type LedgerListProposalsRequest = {
    ledgerId: string;
    status?: 'open' | 'all' | LedgerProposalStatus;
    paymentId?: string | null;
    after?: LedgerProposalsCursor | null;
    limit?: number;
};
export type LedgerListProposalsResponse = { currency: string; proposals: LedgerProposal[]; next: LedgerProposalsCursor | null };
export type LedgerEditProposalRequest = {
    ledgerId: string;
    proposalId: string;
    revision: number;
    lines: { claimId: string; amountMinor: number }[];
    adjustments: LedgerProposalAdjustment[];
};
export type LedgerCommitProposalRequest = { ledgerId: string; proposalId: string; revision: number };
export type LedgerCommitProposalResponse = LedgerProposalResponse & { posted: number; skipped: number };
export type LedgerRejectProposalRequest = LedgerCommitProposalRequest;
/** `details.reason` of a matching callable's refusal. */
export type LedgerProposalRefusalReason =
    | 'openProposalExists'
    | 'revisionChanged'
    | 'closed'
    | 'reversed'
    | 'exceedsOpen'
    | 'exceedsUnapplied'
    | 'exceedsAccrued'
    | 'exceedsReturn'
    | 'negativeClaimAdjustment'
    | 'unplacedCredit'
    | 'unknownClaim'
    | 'undeclaredReason'
    | 'reasonTarget'
    | 'duplicateTarget'
    | 'empty'
    | 'ledgerRefused';

/** A number, trimmed non-empty text, `YYYY-MM-DD` for a date cell at UTC midnight or an ISO instant for any other date, or null. */
export type LedgerStatementCell = string | number | null;
/** One posting row of a statement; `rowNumber` is its 1-based sheet row. */
export type LedgerStatementLine = LedgerRemittanceLineInput & { rowNumber: number };
export type LedgerStatementValueRole = 'total' | 'reference' | 'receivedAt' | 'originalTotal';
/**
 * What reading a statement found, in a fixed order: file, sheet, columns in mapping order, rows ascending, then values.
 * A `totalMismatch` is `withinRounding` when its gap is no more than half a minor unit per rounded line, the total
 * counting as one when it was rounded. An `invalidValue` total or original total carries the amount it read when that
 * amount is zero or less (a summed total has no cell), and null when its cell is not an amount.
 */
export type LedgerStatementProblem =
    | { kind: 'unreadableFile'; message: string }
    | { kind: 'sheetNotFound'; sheet: string }
    | { kind: 'columnNotFound'; header: string }
    | { kind: 'ambiguousColumn'; header: string }
    | { kind: 'unmappedColumn'; header: string }
    | { kind: 'rowSkipped'; rowNumber: number; skipRowIndex: number }
    | { kind: 'invalidAmount'; rowNumber: number; value: LedgerStatementCell }
    | { kind: 'zeroAmount'; rowNumber: number }
    | { kind: 'invalidDimension'; rowNumber: number; dimensionId: string; value: LedgerStatementCell }
    | { kind: 'valueNotFound'; role: LedgerStatementValueRole }
    | { kind: 'ambiguousValue'; role: LedgerStatementValueRole; values: (string | number)[] }
    | { kind: 'invalidValue'; role: 'receivedAt'; value: LedgerStatementCell }
    | { kind: 'invalidValue'; role: 'total' | 'originalTotal'; value: LedgerStatementCell; amountMinor: number | null }
    | { kind: 'tooManyLines'; lines: number; max: number }
    | { kind: 'totalMismatch'; totalMinor: number; linesMinor: number; withinRounding: boolean };
/** Reported but never blocking; every other kind blocks the receipt. */
export const LEDGER_STATEMENT_WARNINGS = ['unmappedColumn', 'rowSkipped', 'zeroAmount', 'totalMismatch'] as const;
/**
 * Why a converted statement is sent back before review: under its mapping's `templateHeadings` or `totalMustMatch`, or,
 * under every mapping, because of its reference or its credits. `offTemplate`: a misspelled heading is a missing one with an extra one
 * in its place; `outOfOrder` names the first template heading present out of the template's order, with its 1-based
 * column in the file and in the template, and is null when the headings present keep the template's order.
 * `creditLines`: a configuration guard, every line with a negative amount on a ledger whose definition names no
 * `creditLineReasonId`, so no credit could be applied at Match. `totalMismatch`:
 * checked only on a file that otherwise reads cleanly, credits counted as lines, and only for a gap of more than half a
 * minor unit per rounded line (the total counting as one when it was rounded). `referenceMangled`: under every mapping, the
 * reference number as text in Excel's scientific notation (`4.26956E+12`), which a CSV saved from Excel writes
 * for a long number and from which the digits cannot be recovered.
 */
export type LedgerStatementRefusal =
    | {
        kind: 'offTemplate';
        missing: string[];
        misspelled: { expected: string; found: string }[];
        extra: string[];
        outOfOrder: { heading: string; foundColumn: number; templateColumn: number } | null;
    }
    | {
        kind: 'creditLines';
        credits: { rowNumber: number; matchGroup: string | null; amountMinor: number }[];
        /** The file's blocking problems, never a `LEDGER_STATEMENT_WARNINGS` kind, so the refusal lists everything left to fix. */
        problems: LedgerStatementProblem[];
    }
    | { kind: 'totalMismatch'; totalMinor: number; linesMinor: number }
    | { kind: 'referenceMangled'; value: string };
/** The converted file a statement import reads; the original statement as received may be any of `LEDGER_ORIGINAL_STATEMENT_EXTENSIONS`. */
export const LEDGER_CONVERTED_STATEMENT_EXTENSIONS = ['xlsx', 'csv'] as const;
export const LEDGER_ORIGINAL_STATEMENT_EXTENSIONS = ['pdf', 'xls', 'xlsx', 'csv'] as const;
/**
 * Size caps in MiB. An original may be larger: a scanned statement is a PDF of page images, megabytes a page, and
 * 50 MiB fits a long scan well inside the confirm callable's 512 MiB.
 */
export const LEDGER_CONVERTED_STATEMENT_MAX_MIB = 10;
export const LEDGER_ORIGINAL_STATEMENT_MAX_MIB = 50;
/**
 * The payment a statement import would post a second time: an unreversed payment, live or archived, with the
 * statement's reference and total, received the same day in the ledger's zone, comparing a placed import's natural
 * `provenance.requestedEffectiveAt`; a placed payment without it matches any day of the period it corrects. It is
 * of the mapping's counterparty or posted from the same original statement as received
 * (`sameReference`), or posted from that original, of the mapping's counterparty or with the statement's reference and
 * total (`sameOriginal`); both may hold. Another counterparty's import from the same original under another reference
 * or total is no repeat, since one statement may pay for several; under the same reference and total it is the same
 * file imported under the wrong mapping. References compare trimmed, lower case, with whitespace runs as one space
 * and an all-digit reference without its leading zeros. `receivedAt` is the payment's received moment, `receivedMinor`
 * its receipt's amount, and `allocated` true when an unreversed allocation or adjustment posts against it, which must be
 * reversed before the payment can be. Read by `ledgerStore.statementRepeat`, never for a file already posted, which is
 * `alreadyImported`, and never naming the payment the import itself would post, which a retried confirm finds there.
 */
export type LedgerStatementRepeat = {
    kind: 'repeatImport';
    paymentId: string;
    reference: string | null;
    receivedAt: string;
    receivedMinor: number;
    allocated: boolean;
    sameReference: boolean;
    sameOriginal: boolean;
};
/** One import from an original statement as received: the counterparty it was imported under and its statement total. */
export type LedgerStatementOriginalImport = { counterpartyId: string; totalMinor: number };
/**
 * An unreversed payment imported from an original statement, with the printed total its receipt cites; null when it
 * cites none. `roundedValues` counts the rounded values its total carries (its rounded lines when a sum of lines, 1 when
 * a printed statement total was rounded); `printedTotalRounded` says whether that printed total carried digits past
 * the minor unit, so was rounded to it.
 */
export type LedgerStatementOriginalPayment = LedgerStatementOriginalImport & {
    paymentId: string;
    roundedValues: number;
    printedTotalMinor: number | null;
    printedTotalRounded: boolean;
};
/**
 * The total printed on an original statement as received, against every import from it: the unreversed payments already
 * standing for it, oldest first, then the file being imported, last. `rounded` counts the printed total, when it
 * carried digits past the minor unit, and every import's rounded values; their sum passes `printedTotalMinor` by at
 * most half a minor unit for each.
 */
export type LedgerStatementOriginalTotal = { printedTotalMinor: number; rounded: number; imports: LedgerStatementOriginalImport[] };
/**
 * Why a statement is refused against its original's printed total. `originalTotalExceeded`: the imports, this file
 * last, come to more than the printed total, by more than half a minor unit per rounded value (`rounded`).
 * `originalTotalMismatch`: this file gives a printed total other than the
 * one an unreversed payment imported from the same original carries (`standing`, the oldest such).
 */
export type LedgerStatementOriginalRefusal =
    | ({ kind: 'originalTotalExceeded' } & LedgerStatementOriginalTotal)
    | {
        kind: 'originalTotalMismatch';
        printedTotalMinor: number;
        standing: { paymentId: string; counterpartyId: string; printedTotalMinor: number };
    };
/**
 * `details.reason` of a statement-import refusal; `offTemplate`, `creditLines`, `totalMismatch`, `referenceMangled`,
 * `repeatImport`, `originalTotalExceeded` and `originalTotalMismatch` also carry `details.refusal`. `originalRejected` is the original statement's own upload refused (missing, wrong type or too large).
 */
export type LedgerStatementImportRefusalReason =
    | 'definitionChanged'
    | 'fileChanged'
    | 'blocked'
    | 'ledgerRefused'
    | 'originalMissing'
    | 'originalSameAsConverted'
    | 'originalRejected'
    | LedgerStatementRefusal['kind']
    | LedgerStatementRepeat['kind']
    | LedgerStatementOriginalRefusal['kind'];
/**
 * `fileFolder` is the `temp/{uid}/{fileId}` folder of an upload holding one `original.xlsx` or `original.csv`.
 * `originalFolder` is the upload folder of the original statement as received, when one was added; propose reads it to
 * refuse a statement already imported from the same original, and confirm also keeps and cites it.
 */
export type LedgerProposeStatementImportRequest = {
    ledgerId: string;
    channelId: string;
    mappingId: string;
    fileFolder: string;
    originalFolder?: string;
};
/**
 * `alreadyImported` wins over `blocked`, which wins over `ready`; `problems` are reported whatever the status.
 * `originalTotal` is set on a `ready` statement sent with its original when this file, or an earlier import from that
 * original, gives the total printed on it.
 */
export type LedgerProposeStatementImportResponse = {
    status: 'ready' | 'blocked' | 'alreadyImported';
    paymentId: string;
    fileHash: string;
    fileName: string;
    definitionRev: number;
    totalMinor: number | null;
    /** The statement total as printed when it carried digits past the minor unit and posts rounded as `totalMinor`; else null. */
    totalAsPrinted: number | null;
    originalTotal: LedgerStatementOriginalTotal | null;
    reference: string | null;
    receivedAt: string | null;
    lines: LedgerStatementLine[];
    problems: LedgerStatementProblem[];
};
/**
 * `fileHash` and `definitionRev` are the propose response's, so the person posts exactly what they saw. `originalFolder`
 * is required when the mapping sets `requireOriginal`.
 */
export type LedgerConfirmStatementImportRequest = LedgerProposeStatementImportRequest & {
    fileHash: string;
    definitionRev: number;
};
/** `placement` is set when the receipt's moment fell in a closed period and it posted in the next open one. */
export type LedgerConfirmStatementImportResponse = {
    status: 'posted' | 'alreadyImported';
    paymentId: string;
    placement: LedgerTransactionPlacement | null;
};

/** Every close ever posted, reversed ones included; reopenedAt is the rev: transaction's posted_at. */
export type LedgerPeriodClose = LedgerPeriodBounds & {
    transactionId: string;
    closedAt: string;
    closedBy: string;
    reopenedAt: string | null;
    reopenedBy: string | null;
};
export type LedgerPeriodSummary = LedgerPeriodBounds & {
    index: number;
    state: 'open' | 'closed';
    /** endAt plus graceDays; null when calendar.close.cycle is 'none'. */
    closesAt: string | null;
    close: { transactionId: string; closedAt: string; closedBy: string } | null;
    reopened: number;
    /** Transactions whose effectiveAt falls in the period, closes and reopens excluded. */
    transactions: number;
    /** Transactions posted later whose correctsPeriod names this period. */
    corrections: number;
};
/** Newest first from the period holding now down to the period holding calendar.startAt; limit defaults to 12, clamped 1..60. */
export type LedgerListPeriodsRequest = { ledgerId: string; beforeIndex?: number | null; limit?: number };
export type LedgerListPeriodsResponse = {
    timeZone: string;
    unit: LedgerFiscalPeriodUnit;
    cycle: 'none' | 'period';
    graceDays: number;
    periods: LedgerPeriodSummary[];
    next: number | null;
};
export type LedgerClosePeriodRequest = { ledgerId: string; periodKey: string };
export type LedgerClosePeriodResponse = { status: 'closed' | 'alreadyClosed'; transactionId: string };
export type LedgerReopenPeriodRequest = LedgerClosePeriodRequest;
export type LedgerReopenPeriodResponse = { status: 'reopened' | 'alreadyOpen'; transactionId: string | null };
/** details.reason of a close or reopen refusal. */
export type LedgerPeriodRefusalReason = 'cycleNone' | 'beforeStart' | 'notEnded' | 'ledgerRefused';

export type LedgerLegalHoldScope =
    | { scope: 'ledger' }
    | { scope: 'counterparty'; counterpartyId: string }
    | { scope: 'period'; periodKey: string };
/** A hold blocks both archive and purge of every closure set it covers until it is released. */
export type LedgerLegalHold = LedgerLegalHoldScope & {
    holdId: string;
    reason: string;
    placedBy: string;
    placedAt: string;
    releasedBy: string | null;
    releasedAt: string | null;
};
/** One ledger's result of one due run of `ledgerScheduledLifecycle`; a failed run holds the counts reached before it failed. */
export type LedgerLifecycleRun = {
    runId: string;
    startedAt: string;
    finishedAt: string;
    status: 'succeeded' | 'failed';
    error: string | null;
    archive: { archived: number; held: number; changed: number; transactions: number };
    purge: { purged: number; held: number; reopened: number; transactions: number };
    proposalsDeleted: number;
    truncated: boolean;
};
export type LedgerPlaceLegalHoldRequest = { ledgerId: string; hold: LedgerLegalHoldScope; reason: string };
export type LedgerPlaceLegalHoldResponse = { holdId: string };
export type LedgerReleaseLegalHoldRequest = { ledgerId: string; holdId: string };
export type LedgerReleaseLegalHoldResponse = { status: 'released' | 'alreadyReleased' };
export type LedgerListLegalHoldsRequest = { ledgerId: string; includeReleased?: boolean };
export type LedgerListLegalHoldsResponse = { holds: LedgerLegalHold[] };
/** `limit` defaults to 50, at most 200; newest first. */
export type LedgerListLifecycleRunsRequest = { ledgerId: string; limit?: number };
export type LedgerListLifecycleRunsResponse = { runs: LedgerLifecycleRun[] };
